import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  IconButton,
  Typography,
  Box,
  Skeleton,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import {
  DragIndicator as DragIcon,
  ContentCopy as CopyIcon,
} from "@mui/icons-material";
import { ModalProps } from "@_constants/modal";
import { type TabData } from "@utils/dashboard-storage";
import { useTabManagement } from "@/hooks/use-tab-management";
import { useCreateDashboardTabMutation } from "@/store/api/dashboard-api";
import HPButton from "../common/Button";
import {
  CloseIcon,
  PlusIconBlue,
  RemoveIcon,
  RemoveIconRed,
  WarningIconBlack,
} from "@/assets/images";

interface TabManagementModalProps extends ModalProps {
  tabs: TabData[];
  onSave: (tabs: TabData[]) => void;
  onAddTab: (name: string) => void;
  onRemoveTab?: (tabId: string) => void;
  onDuplicateTab?: (tabId: string, newName: string) => void;
  dashboardId?: string;
  shouldStartInAddMode?: boolean;
  isDuplicatingTab?: boolean;
}

const TabManagementModal: React.FC<TabManagementModalProps> = ({
  tabs,
  onSave,
  onAddTab,
  onRemoveTab,
  onDuplicateTab,
  dashboardId,
  closeAction,
  shouldStartInAddMode,
  isDuplicatingTab = false,
}) => {
  const [, { isLoading: isCreatingTab }] = useCreateDashboardTabMutation({
    fixedCacheKey: `create-tab-${dashboardId}`,
  });

  const {
    // State
    localTabs,
    newTabName,
    setNewTabName,
    newTabError,
    tabErrors,
    draggedIndex,
    isAddingNewTab,
    hoveredTabId,
    deleteConfirmation,
    hoveredNewItem,
    isLoadingTabs,

    // Refs
    newTabInputRef,

    // Handlers
    handleUpdateTabName,
    handleTabNameBlur,
    handleAddTab,
    handleSaveNewTab,
    handleCancelNewTab,
    handleDeleteTabClick,
    confirmDeleteTab,
    cancelDeleteTab,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    handleMouseEnter,
    handleMouseLeave,
    handleNewItemMouseEnter,
    handleNewItemMouseLeave,
    handleDuplicateTab,
  } = useTabManagement({
    tabs,
    onSave,
    onAddTab,
    onRemoveTab,
    onDuplicateTab,
    dashboardId,
    shouldStartInAddMode,
  });

  return (
    <Dialog
      open={true}
      onClose={closeAction}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: 400,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="subtitle1" component="div">
          Tabs
        </Typography>
        <IconButton onClick={closeAction} sx={{ p: 0.5 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2, position: "relative" }}>
        <Backdrop
          sx={{
            position: "absolute",
            color: "#fff",
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: "rgba(255, 255, 255, 0.95)",
          }}
          open={isDuplicatingTab}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <CircularProgress />
          </Box>
        </Backdrop>
        <List sx={{ position: "relative", zIndex: isDuplicatingTab ? 0 : 1 }}>
          {localTabs.map((tab, index) => (
            <ListItem
              key={tab.id}
              draggable={
                !deleteConfirmation || deleteConfirmation.tabId !== tab.id
              }
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnd={handleDragEnd}
              onDragOver={handleDragOver}
              onDragEnter={(e) => handleDragEnter(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              sx={{
                p: 0,
                cursor:
                  deleteConfirmation && deleteConfirmation.tabId === tab.id
                    ? "default"
                    : "grab",
                opacity: draggedIndex === index ? 0.5 : 1,
                mb: "10px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  padding: "0 16px",
                }}
              >
                <Box sx={{ flexGrow: 1, my: 1 }}>
                  {deleteConfirmation && deleteConfirmation.tabId === tab.id ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 12px",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <WarningIconBlack />
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          flexGrow: 1,
                          fontSize: "14px",
                        }}
                      >
                        {deleteConfirmation.hasContent
                          ? "There are widgets in this tab. Are you sure you want to delete it?"
                          : "Are you sure you want to delete this tab?"}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 0 }}>
                        <HPButton
                          variant="text"
                          onClick={() => confirmDeleteTab(true)}
                          className="px-[0px] font-bold"
                          color="error"
                        >
                          Delete
                        </HPButton>
                        <HPButton
                          variant="text"
                          onClick={cancelDeleteTab}
                          className="px-[0px] font-bold"
                        >
                          Cancel
                        </HPButton>
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        padding: "0 16px",
                      }}
                    >
                      <DragIcon sx={{ mr: 2, color: "text.secondary" }} />

                      <TextField
                        size="small"
                        value={tab.label}
                        onChange={(e) =>
                          handleUpdateTabName(tab.id, e.target.value)
                        }
                        onBlur={(e) =>
                          handleTabNameBlur(tab.id, e.target.value)
                        }
                        inputProps={{ maxLength: 40 }}
                        fullWidth
                        error={!!tabErrors[tab.id]}
                        helperText={tabErrors[tab.id]}
                        InputProps={{
                          sx: {
                            borderRadius: "8px",
                            position: "relative",
                          },
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          right: "80px",
                          top: "50px",
                          position: "absolute",
                        }}
                      >
                        {tab.label.length}/40
                      </Typography>
                    </Box>
                  )}
                </Box>
                {!(
                  deleteConfirmation && deleteConfirmation.tabId === tab.id
                ) && (
                  <>
                    <IconButton
                      aria-label="delete"
                      onClick={() => handleDeleteTabClick(tab.id)}
                      disabled={localTabs.length <= 1}
                      onMouseEnter={() => handleMouseEnter(tab.id)}
                      onMouseLeave={handleMouseLeave}
                      sx={{
                        ml: 1,
                        color: "text.secondary",
                        "&:hover": {
                          color: "#D80000",
                        },
                      }}
                    >
                      {hoveredTabId === tab.id ? (
                        <RemoveIconRed />
                      ) : (
                        <RemoveIcon />
                      )}
                    </IconButton>
                    <IconButton
                      aria-label="duplicate"
                      onClick={() => handleDuplicateTab?.(tab.id, tab.label)}
                      disabled={isDuplicatingTab}
                      sx={{
                        ml: 1,
                        color: "black",
                        "&:hover": {
                          color: isDuplicatingTab ? "inherit" : "primary.main",
                        },
                      }}
                      title="Duplicate tab"
                    >
                      <CopyIcon className="w-[24px]" />
                    </IconButton>
                  </>
                )}
              </Box>
            </ListItem>
          ))}
          {isAddingNewTab && !isCreatingTab && !isLoadingTabs && (
            <ListItem sx={{ p: 0, mb: "10px" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  padding: "0 16px",
                  mt: "10px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    padding: "0 16px",
                  }}
                >
                  <DragIcon sx={{ mr: 2, color: "text.secondary" }} />
                  <TextField
                    size="small"
                    value={newTabName}
                    onChange={(e) => setNewTabName(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleSaveNewTab();
                      }
                    }}
                    onBlur={handleSaveNewTab}
                    inputProps={{ maxLength: 40 }}
                    fullWidth
                    error={!!newTabError}
                    helperText={newTabError}
                    InputProps={{
                      sx: {
                        borderRadius: "8px",
                        position: "relative",
                      },
                    }}
                    inputRef={newTabInputRef}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      right: "80px",
                      top: "50px",
                      position: "absolute",
                    }}
                  >
                    {newTabName.length}/40
                  </Typography>
                </Box>

                <IconButton
                  aria-label="cancel"
                  onClick={handleCancelNewTab}
                  onMouseEnter={handleNewItemMouseEnter}
                  onMouseLeave={handleNewItemMouseLeave}
                  sx={{
                    ml: 1,
                    color: "text.secondary",
                    "&:hover": {
                      color: "#D80000",
                    },
                  }}
                >
                  {hoveredNewItem ? <RemoveIconRed /> : <RemoveIcon />}
                </IconButton>
              </Box>
            </ListItem>
          )}
          {isAddingNewTab && (isLoadingTabs || isCreatingTab) && (
            <ListItem sx={{ p: 0, mb: "10px", opacity: 0.6 }}>
              <TabSkeleton />
            </ListItem>
          )}
        </List>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          flexDirection: "row-reverse",
          gap: "8px",
        }}
        className="flex flex-start "
      >
        <Typography variant="subtitle2" className="text-[#A6A6A6]">
          Max 20 tabs
        </Typography>

        <HPButton
          ariaLabel="Manage Tabs"
          variant="outlined"
          size="small"
          onClick={handleAddTab}
          startIcon={<PlusIconBlue />}
          disabled={localTabs.length >= 20 || isAddingNewTab}
        >
          Add Tab
        </HPButton>
      </DialogActions>
    </Dialog>
  );
};

export default TabManagementModal;

const TabSkeleton: React.FC = () => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        width: "100%",
        padding: "0 16px",
      }}
    >
      <Box sx={{ flexGrow: 1, my: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            padding: "0 16px",
          }}
        >
          <DragIcon sx={{ mr: 2, color: "text.disabled" }} />
          <Skeleton
            variant="rounded"
            height={40}
            sx={{ flexGrow: 1, borderRadius: "8px" }}
          />
        </Box>
      </Box>
      <Skeleton variant="circular" width={40} height={40} sx={{ ml: 1 }} />
    </Box>
  );
};
